export function initFaqPage() {
    const roots = document.querySelectorAll('[data-faq-page]');
    if (!roots.length) return;

    roots.forEach((root) => {
        const buttons = Array.from(root.querySelectorAll('[data-faq-filter]'));
        const mobileFilter = root.querySelector('[data-faq-mobile-filter]');
        const mobileTrigger = root.querySelector('[data-faq-mobile-trigger]');
        const mobileMenu = root.querySelector('[data-faq-mobile-menu]');
        const mobileLabel = root.querySelector('[data-faq-mobile-label]');
        const mobileOptions = Array.from(root.querySelectorAll('[data-faq-mobile-option]'));
        const groups = Array.from(root.querySelectorAll('[data-faq-group]'));
        const triggers = Array.from(root.querySelectorAll('[data-faq-trigger]'));
        const answerAnimMs = 280;
        const panelToTrigger = new Map();
        let scrollAnimationFrame = null;
        const cancelSmoothScroll = () => {
            if (scrollAnimationFrame) {
                window.cancelAnimationFrame(scrollAnimationFrame);
                scrollAnimationFrame = null;
            }
        };
        const smoothScrollTo = (targetY, duration = 900) => {
            cancelSmoothScroll();

            const startY = window.scrollY;
            const distance = targetY - startY;
            if (Math.abs(distance) < 1) return;

            const startTime = performance.now();
            const easeInOutCubic = (t) => (
                t < 0.5
                    ? 4 * t * t * t
                    : 1 - Math.pow(-2 * t + 2, 3) / 2
            );

            const step = (now) => {
                const elapsed = now - startTime;
                const progress = Math.min(elapsed / duration, 1);
                const easedProgress = easeInOutCubic(progress);
                window.scrollTo(0, startY + distance * easedProgress);

                if (progress < 1) {
                    scrollAnimationFrame = window.requestAnimationFrame(step);
                } else {
                    scrollAnimationFrame = null;
                }
            };

            scrollAnimationFrame = window.requestAnimationFrame(step);
        };

        const scrollToFaqTop = () => {
            const header = document.querySelector('.site-header');
            const headerOffset = header ? header.offsetHeight + 16 : 24;
            const targetTop = Math.max(
                root.getBoundingClientRect().top + window.scrollY - headerOffset,
                0
            );

            smoothScrollTo(targetTop, 1000);
        };

        triggers.forEach((trigger) => {
            const item = trigger.closest('[data-faq-entry]');
            const panel = item?.querySelector('[data-faq-entry-panel]');
            if (panel) {
                panelToTrigger.set(panel, trigger);
            }
        });

        const hideGroup = (group) => {
            group.hidden = true;
            group.classList.add('is-hidden');
            group.style.opacity = '0';
        };

        const showGroup = (group, immediate = false) => {
            if (immediate) {
                group.hidden = false;
                group.classList.remove('is-hidden');
                group.style.opacity = '1';
                return;
            }

            group.hidden = false;
            group.classList.remove('is-hidden');
            group.style.opacity = '0';

            window.requestAnimationFrame(() => {
                group.style.opacity = '1';
            });
        };

        const setFilter = (value, immediate = false) => {
            const selected = value || 'all';

            groups.forEach((group) => {
                const id = group.getAttribute('data-faq-group');
                const visible = selected === 'all' || id === selected;

                if (visible) {
                    showGroup(group, immediate);
                } else {
                    hideGroup(group);
                }

                group.setAttribute('aria-hidden', visible ? 'false' : 'true');
            });

            buttons.forEach((button) => {
                const active = button.getAttribute('data-faq-filter') === selected;
                button.classList.toggle('is-active', active);
                button.setAttribute('aria-pressed', active ? 'true' : 'false');
            });

            mobileOptions.forEach((option) => {
                const active = option.getAttribute('data-faq-mobile-option') === selected;
                option.classList.toggle('is-active', active);
                option.hidden = active;
                option.setAttribute('aria-hidden', active ? 'true' : 'false');

                if (active && mobileLabel) {
                    mobileLabel.textContent = option.textContent?.trim() || "All FAQS";
                }
            });
        };

        const closeMobileMenu = () => {
            if (!mobileTrigger || !mobileMenu || !mobileFilter) return;
            mobileTrigger.setAttribute('aria-expanded', 'false');
            mobileFilter.classList.remove('is-open');
            mobileMenu.hidden = true;
        };

        const openMobileMenu = () => {
            if (!mobileTrigger || !mobileMenu || !mobileFilter) return;
            mobileTrigger.setAttribute('aria-expanded', 'true');
            mobileFilter.classList.add('is-open');
            mobileMenu.hidden = false;
        };

        const toggleMobileMenu = () => {
            if (!mobileTrigger) return;
            const isOpen = mobileTrigger.getAttribute('aria-expanded') === 'true';
            if (isOpen) {
                closeMobileMenu();
            } else {
                openMobileMenu();
            }
        };

        buttons.forEach((button) => {
            button.addEventListener('click', () => {
                setFilter(button.getAttribute('data-faq-filter'));
                scrollToFaqTop();
            });
        });

        if (mobileTrigger) {
            mobileTrigger.addEventListener('click', () => {
                toggleMobileMenu();
            });
        }

        mobileOptions.forEach((option) => {
            option.addEventListener('click', () => {
                const value = option.getAttribute('data-faq-mobile-option') || 'all';
                setFilter(value);
                closeMobileMenu();
                scrollToFaqTop();
            });
        });

        document.addEventListener('click', (event) => {
            if (!mobileFilter || !mobileTrigger) return;
            if (!mobileFilter.contains(event.target)) {
                closeMobileMenu();
            }
        });

        document.addEventListener('keydown', (event) => {
            if (event.key === 'Escape') {
                closeMobileMenu();
            }
        });

        const closePanel = (panel) => {
            const trigger = panelToTrigger.get(panel);
            if (!trigger) return;

            if (panel.dataset.answerTimer) {
                window.clearTimeout(Number(panel.dataset.answerTimer));
                delete panel.dataset.answerTimer;
            }

            trigger.setAttribute('aria-expanded', 'false');
            panel.style.maxHeight = `${panel.scrollHeight}px`;
            panel.classList.remove('is-open');

            window.requestAnimationFrame(() => {
                panel.style.maxHeight = '0px';
            });

            const timer = window.setTimeout(() => {
                panel.hidden = true;
                delete panel.dataset.answerTimer;
            }, answerAnimMs);

            panel.dataset.answerTimer = String(timer);
        };

        const openPanel = (panel) => {
            const trigger = panelToTrigger.get(panel);
            if (!trigger) return;

            if (panel.dataset.answerTimer) {
                window.clearTimeout(Number(panel.dataset.answerTimer));
                delete panel.dataset.answerTimer;
            }

            trigger.setAttribute('aria-expanded', 'true');
            panel.hidden = false;
            panel.classList.add('is-open');
            panel.style.maxHeight = '0px';

            window.requestAnimationFrame(() => {
                panel.style.maxHeight = `${panel.scrollHeight}px`;
            });

            window.setTimeout(() => {
                if (trigger.getAttribute('aria-expanded') === 'true') {
                    panel.style.maxHeight = 'none';
                }
            }, answerAnimMs);
        };

        triggers.forEach((trigger) => {
            trigger.addEventListener('click', () => {
                cancelSmoothScroll();
                const expanded = trigger.getAttribute('aria-expanded') === 'true';
                const item = trigger.closest('[data-faq-entry]');
                const panel = item?.querySelector('[data-faq-entry-panel]');
                if (!panel) return;

                if (expanded) {
                    closePanel(panel);
                    return;
                }

                // Keep accordion exclusive: close any open panel first.
                panelToTrigger.forEach((_ownerTrigger, candidatePanel) => {
                    if (candidatePanel !== panel && !candidatePanel.hidden) {
                        closePanel(candidatePanel);
                    }
                });

                openPanel(panel);
            });
        });

        setFilter('all', true);
        closeMobileMenu();
    });
}
